import { FC, useEffect, useMemo, useState } from 'react'
import { translate } from '../../../core'
import { Space, Table, Image, Button, Popover } from 'antd'
import { Item, ItemImage } from '../type'
import { gql, TypedDocumentNode, useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { ItemFindRequest } from '../type/item-query'
import { resolvePathFile } from '../../../core/until'
import { EllipsisOutlined } from '@ant-design/icons'
import Enumerable, { IEnumerable } from 'linq'
import { ShowIf } from '../../../core/component'

export type ItemCategoryItemsListProps = {
  categoryId?: number
  subcategoryId?: number
  subcategory2Id?: number
}

const GetItems: TypedDocumentNode<QueryPageableResponse<Item, 'itemsFind'>, QueryPageableRequest<ItemFindRequest>> = gql`
    query GetItems($request: ItemFindRequest!) {
        itemsFind(request: $request) {
            data {
                id
                sku
                type
                description
                images {
                    filename
                    priority
                }
            }
            count
        }
    }
`

const ItemImages: FC<{ images: ItemImage[], enabledPopover?: boolean }> = ({ images, enabledPopover }) => {
  const topImages = useMemo(() => {
    let imageEumerable: IEnumerable<ItemImage> = Enumerable.from(images).orderBy(im => im.priority)
    if (!enabledPopover)
      imageEumerable = imageEumerable.take(2)
    return imageEumerable.toArray()
  }, [images, enabledPopover])

  return (
    <Space
      wrap={enabledPopover}
    >
      {topImages.map((image, index) => (
        <Image
          key={index}
          width={50}
          height={50}
          src={resolvePathFile(image.filename)}
        />
      ))}
      <ShowIf
        condition={!enabledPopover && images.length > 2}
        replacement={<></>}
      >
        <Popover
          placement='bottom'
          content={<ItemImages images={images.filter(im => !topImages.includes(im))} enabledPopover />}
        >
          <Button
            type='text'
            shape='circle'
            icon={<EllipsisOutlined style={{ fontSize: 25 }} />}
          />
        </Popover>
      </ShowIf>
    </Space>
  )
}

export const ItemCategoryItemsList: FC<ItemCategoryItemsListProps> = ({ categoryId, subcategoryId, subcategory2Id }) => {
  const [pageSize, setPageSize] = useState<number>(15)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const { data, loading } = useQuery(GetItems, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
        categoryId: categoryId ? [categoryId] : undefined,
        subcategoryId: subcategoryId ? [subcategoryId] : undefined,
        subcategory2Id: subcategory2Id ? [subcategory2Id] : undefined,
      }
    }
  })

  useEffect(() => {
    setCurrentPage(0)
  }, [categoryId, subcategoryId, subcategory2Id, pageSize])

  return (
    <Table<Item>
      loading={loading}
      size='small'
      columns={[
        {
          dataIndex: 'sku',
          title: translate('sku'),
          width: 150,
          render: (value: string, record: Item) => (
            <Link to={`/inventory/${record.sku}`}>{value}</Link>
          ),
        },
        {
          dataIndex: 'description',
          title: translate('description'),
          width: 200,
          ellipsis: true,
        },
        {
          dataIndex: 'images',
          title: translate('images'),
          width: 150,
          render: (value: ItemImage[]) => <ItemImages images={value} />,
        },
      ]}
      dataSource={(data?.itemsFind.data ?? []).map(it => ({ ...it, key: it.id.toString() }))}
      pagination={{
        total: data?.itemsFind.count,
        position: ['bottomCenter'],
        pageSize: pageSize,
        showSizeChanger: false,
        current: currentPage + 1,
      }}
      onChange={(pagination) => {
        if (pagination.current)
          setCurrentPage(pagination.current - 1)

        if (pagination.pageSize)
          setPageSize(pagination.pageSize)
      }}
    />
  )
}
